import React, { useCallback, useEffect, useState } from 'react'
import { Col, OverlayTrigger, Row } from 'react-bootstrap'
import BootstrapTooltip from 'react-bootstrap/Tooltip'

import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { ReactComponent as ZocksLogo } from '../../assets/ZocksLogo.svg'
import { useExportConfirmation } from '../../components/ConfirmationDialog/useExportConfirmation'
import { useExportToPDF } from '../../components/PDFExportButton/hooks'
import { Button } from '../../components/Button/Button'
import { Modal } from '../../components/Modal/Modal'
import { useGetMeetingTemplatesQuery } from '../../store/ai/meetingTemplate'
import { useLazyGetAiAgentResultQuery } from '../../store/ai/aiAgentResult'
import { useExecuteAiAgentMutation } from '../../store/ai/aiAgent'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { MeetingPrepExecutionResult } from '../../models/MeetingPrep'
import { AIAgentType } from '../../models/AIAgent'

import { MeetingPrepBody, MeetingPrepModalHeader, FooterComponentProps, MEETING_PREP_PDF_STYLES } from './MeetingPrep'
import { AIAgentLastUpdated } from './AIAgentLastUpdated/AIAgentLastUpdated'
import { useMeetingPrep } from './hooks/useMeetingPrep'

import './MeetingPrep.scss'

export interface ContactMeetingPrepModalProps {
  contactId: string
  onHide: () => void
  show: boolean
}

export const ContactMeetingPrepModal: React.FC<ContactMeetingPrepModalProps> = ({ contactId, onHide, show }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()
  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [meetingTemplateId, setMeetingTemplateId] = useState<string | null>(null)
  const [showChatPage, setShowChatPage] = useState<boolean>(false)

  const executeMeetingPrep = useCallback(
    (selectedMeetingTemplateId: string) =>
      triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.MEETING_PREPARATION,
          context: {
            type: 'contact',
            contactId,
            meetingTemplateId: selectedMeetingTemplateId,
          },
        },
      }).unwrap() as Promise<MeetingPrepExecutionResult>,
    [triggerExecuteAiAgent, accountId, contactId],
  )

  const getExistingMeetingPrep = useCallback(
    () =>
      triggerGetAiAgentResult(
        {
          accountId,
          type: AIAgentType.MEETING_PREPARATION,
          resultKey: `contact|${contactId}`,
        },
        false,
      ).unwrap(),
    [triggerGetAiAgentResult, accountId, contactId],
  )

  const {
    isLoading,
    interactionIsLoading,
    editIsLoading,
    existingMeetingPrep,
    meetingPrep,
    onMeetingPrepChatInteraction,
    onMeetingPrepEditInteraction,
    handleResubmitInteraction,
    onMeetingPrepExecution,
    aiAgentContext,
    citations,
    citationIsLoading,
  } = useMeetingPrep({ executeMeetingPrep, getExistingMeetingPrep })

  useEffect(() => {
    if (isLoading || existingMeetingPrep || meetingPrep) {
      return
    }
    setShowChatPage(true)
  }, [isLoading, existingMeetingPrep, meetingPrep])

  useEffect(() => {
    if (!aiAgentContext?.meetingTemplateId) {
      return
    }
    setMeetingTemplateId(aiAgentContext.meetingTemplateId)
  }, [aiAgentContext])

  const trackingEventDetails = {
    source: 'contact',
    contactId,
    meetingTemplateId,
  }

  const _handleRerun = useCallback(async () => {
    if (!meetingTemplateId) {
      return
    }
    await onMeetingPrepExecution(meetingTemplateId)
  }, [meetingTemplateId, onMeetingPrepExecution])

  const handleRerun = tracked(_handleRerun, {
    eventName: TrackEvent.MEETING_PREP_RERUN,
    eventDetails: trackingEventDetails,
  })

  const handleReturn = () => {
    setShowChatPage(true)
  }

  const _handleMeetingTemplateSelected = async (selectedMeetingTemplateId: string) => {
    setShowChatPage(false)
    await onMeetingPrepExecution(selectedMeetingTemplateId)
  }

  const handleMeetingTemplateSelected = tracked(_handleMeetingTemplateSelected, {
    eventName: TrackEvent.MEETING_PREP_EXECUTED_ON_CONTACT,
    eventDetails: trackingEventDetails,
  })

  const FooterComponent: React.FC<FooterComponentProps> = ({ meetingPrepBody }) => {
    const { exportToPDF } = useExportToPDF(meetingPrepBody, MEETING_PREP_PDF_STYLES)
    const { ConfirmationModal, runMutationWithConfirmation, accountIsLoading } = useExportConfirmation({ exportType: 'document' })

    const handleExportToPDF = async () =>
      await runMutationWithConfirmation(
        tracked(exportToPDF, {
          eventName: TrackEvent.EXPORT_TO_PDF,
          eventDetails: {
            contactId: contactId,
            type: 'meetingPrep',
          },
        }),
      )

    return (
      <>
        <ConfirmationModal />
        <Row className='py-4'>
          <Col className='p-0 d-flex align-items-center justify-content-between'>
            <div className='d-flex align-items-center gap-3'>
              <Button className='button-medium py-0' onClick={handleReturn}>
                <i className='bi bi-arrow-return-left' />
                Choose a different meeting type
              </Button>
              <Button className='button-medium py-0' onClick={handleExportToPDF} disabled={accountIsLoading}>
                <i className='bi bi-file-pdf' /> Export to PDF
              </Button>
              <OverlayTrigger
                trigger={citationIsLoading ? ['hover', 'focus'] : []}
                placement='top'
                overlay={
                  <BootstrapTooltip className='zocks-text-gray-500'>Rerun is disabled while references are being generated. Please hold on.</BootstrapTooltip>
                }
              >
                <Button className='button-medium py-0' onClick={handleRerun} disabled={citationIsLoading}>
                  <i className='bi bi-arrow-clockwise' />
                  Rerun
                </Button>
              </OverlayTrigger>
            </div>
            <AIAgentLastUpdated existingAgentResult={existingMeetingPrep} />
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={!showChatPage ? 'xl' : undefined}
      header={<MeetingPrepModalHeader />}
      body={
        showChatPage ? (
          <ContactMeetingPrepChatPage isLoading={isLoading} handleMeetingTemplateSelected={handleMeetingTemplateSelected} />
        ) : (
          <MeetingPrepBody
            isLoading={isLoading}
            interactionIsLoading={interactionIsLoading}
            editIsLoading={editIsLoading}
            meetingPrep={meetingPrep}
            onMeetingPrepChatInteraction={onMeetingPrepChatInteraction}
            onMeetingPrepEditInteraction={onMeetingPrepEditInteraction}
            handleResubmitInteraction={handleResubmitInteraction}
            trackingEventDetails={trackingEventDetails}
            FooterComponent={FooterComponent}
            citations={citations}
            citationIsLoading={citationIsLoading}
          />
        )
      }
    />
  )
}

interface ContactMeetingPrepChatPageProps {
  isLoading: boolean
  handleMeetingTemplateSelected: (meetingTemplateId: string) => void
}

const ContactMeetingPrepChatPage: React.FC<ContactMeetingPrepChatPageProps> = ({ handleMeetingTemplateSelected }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: meetingTemplates } = useGetMeetingTemplatesQuery({
    accountId,
    pageRequest: { page: 0, size: 500 },
    includeInactive: false,
  })

  return (
    <div className='mb-4 d-flex flex-column gap-3'>
      <div className='d-flex py-3 px-2 align-items-center gap-3 ai-agent-chat-header'>
        <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-header-logo zocks-border-0.25'>
          <ZocksLogo width={18} height={18} />
        </div>
        <div className='d-flex flex-column'>
          <span className='paragraph-small'>ZOCKS</span>
          <span className='paragraph-small'>AI Assistant</span>
        </div>
      </div>
      <div className='d-flex flex-column justify-content-between gap-3 flex-grow-1 overflow-y-auto ai-agent-messages-container'>
        <div className='d-flex zocks-gap-0.75 px-2'>
          <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
            <ZocksLogo width={12} height={12} />
          </div>
          <div className='ai-agent-chat-message paragraph-medium'>Welcome! Please tell me what kind of meeting you are preparing for!</div>
        </div>
        {meetingTemplates?.data ? (
          <div className='d-flex flex-column gap-2'>
            <span className='lead-small text-uppercase'>Choose meeting type</span>
            <div className='d-flex flex-wrap gap-2 align-items-start'>
              {meetingTemplates.data.map(({ id, name }) => (
                <Button
                  key={id}
                  className='zocks-py-0.125 px-2 ai-agent-example paragraph-medium mw-0 rounded-5'
                  onClick={() => handleMeetingTemplateSelected(id)}
                >
                  {name}
                </Button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
